<template>
  <div id="stats" class="row g-2 justify-content-center my-5">
    <div class="col-xxl-6 col-xl-7 col-lg-8 col-md-11 hyphenate">
      <div>
        <h1>Übersicht über die verwendeten Daten</h1>
        <p>
          Unser Machine Learning System wird auf echten Verspätungsinformationen trainiert. Diese
          Daten werden rund um die Uhr von uns aktualisiert. Hier gibt es eine Übersicht über den
          Datensatz.
        </p>
      </div>
      <div v-if="Object.keys(stats).length" class="text-center">
        Letzte Aktuallisierung der Statistik:
        {{ dayjs(stats.stats_creation_ts).tz('Europe/Berlin').format('DD.MM.YYYY HH:mm') }}
        <div class="stats-overview">
          <div class="stath header"></div>
          <div class="header">Ankunft</div>
          <div class="header">Abfahrt</div>

          <!-- All Data -->
          <div class="vertical-header">Daten seit Oktober 2021</div>
          <div class="stat header">Anzahl an Halten</div>
          <div>
            {{ formatNumber(stats.all_ar_count) }}
          </div>
          <div>
            {{ formatNumber(stats.all_dp_count) }}
          </div>

          <div class="stat header">Maximale Verspätung</div>
          <div>{{ formatNumber(stats.all_ar_max_delay) }} min</div>
          <div>{{ formatNumber(stats.all_dp_max_delay) }} min</div>

          <div class="stat header">Ø Verspätung</div>
          <div>{{ formatNumber(stats.all_ar_avg_delay) }} min</div>
          <div>{{ formatNumber(stats.all_dp_avg_delay) }} min</div>

          <div class="stat header">Pünktlichkeit</div>
          <div>{{ formatPercent(stats.all_ar_on_time) }}</div>
          <div>{{ formatPercent(stats.all_dp_on_time) }}</div>

          <div class="stat header">Ausfälle</div>
          <div>{{ formatPercent(stats.all_ar_cancelled) }}</div>
          <div>{{ formatPercent(stats.all_dp_cancelled) }}</div>

          <div class="rowspan divider m-1 p-0"></div>

          <!-- Most recent month of data -->
          <div class="vertical-header">
            {{ dayjs(stats.month_start).tz('Europe/Berlin').format('MMMM YYYY') }}
          </div>
          <div class="stat header">Anzahl an Halten</div>
          <div>
            {{ formatNumber(stats.month_ar_count) }}
          </div>
          <div>
            {{ formatNumber(stats.month_dp_count) }}
          </div>

          <div class="stat header">Maximale Verspätung</div>
          <div>{{ formatNumber(stats.month_ar_max_delay) }} min</div>
          <div>{{ formatNumber(stats.month_dp_max_delay) }} min</div>

          <div class="stat header">Ø Verspätung</div>
          <div>{{ formatNumber(stats.month_ar_avg_delay) }} min</div>
          <div>{{ formatNumber(stats.month_dp_avg_delay) }} min</div>

          <div class="stat header">Pünktlichkeit</div>
          <div>{{ formatPercent(stats.month_ar_on_time) }}</div>
          <div>{{ formatPercent(stats.month_dp_on_time) }}</div>

          <div class="stat header">Ausfälle</div>
          <div>{{ formatPercent(stats.month_ar_cancelled) }}</div>
          <div>{{ formatPercent(stats.month_dp_cancelled) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: 'default' })

useHead({
  title: 'Übersichtsstatistik',
  meta: [
    {
      name: 'description',
      content:
        'Übersichtstatistik über die Daten des Projekts Bahn-Vorhersage. Durchschnittliche Verspätungs- und Ausfallwerte sowie die Anzahl der gesammelten Datenpunkte.'
    }
  ]
})

import { useMainStore } from '~/stores/main'
import { onMounted, ref } from 'vue'
import dayjs from 'dayjs'

type Stats = {
  month_start: string
  month_end: string
  stats_creation_ts: string
  month_ar_max_delay: number
  month_ar_avg_delay: number
  month_ar_count: number
  month_ar_cancelled: number
  month_ar_on_time: number
  month_dp_max_delay: number
  month_dp_avg_delay: number
  month_dp_count: number
  month_dp_cancelled: number
  month_dp_on_time: number
  all_ar_max_delay: number
  all_ar_avg_delay: number
  all_ar_count: number
  all_ar_cancelled: number
  all_ar_on_time: number
  all_dp_max_delay: number
  all_dp_avg_delay: number
  all_dp_count: number
  all_dp_cancelled: number
  all_dp_on_time: number
}

function formatPercent(value: number): string {
  return (
    (value * 100).toLocaleString('de-DE', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }) + ' %'
  )
}

function formatNumber(value: number): string {
  return value.toLocaleString('de-DE', { maximumFractionDigits: 2 })
}

const store = useMainStore()
const config = useRuntimeConfig()

const stats = ref<Stats>({} as Stats)

onMounted(() => {
  fetch(config.public.requestBaseUrl + 'api/stats')
    .then((response) => store.displayFetchError(response))
    .then((response) => response.json())
    .then((response) => {
      stats.value = response
    })
})
</script>

<style lang="scss">
.stats-overview {
  @include border-radius;
  display: grid;
  grid-template-columns: min-content repeat(3, auto);
  gap: 5px;

  div {
    @include border-radius;
    text-align: center;
    vertical-align: middle;
    background-color: $light-bg-subtle-dark;
    padding: 10px;
    display: flex;
    justify-content: center;
    flex-direction: column;
  }

  .stath {
    grid-area: 1 / 1 / 1 / 3;
  }

  .header {
    font-size: 1.1rem;
    font-weight: bold;
  }

  .rowspan {
    grid-column-start: span 4;
  }

  .vertical-header {
    grid-row-start: span 5;
    width: calc(1.5em + 30px);
    writing-mode: vertical-rl;
    transform: scale(-1, -1);
  }
}

@media (max-width: 740px) {
  .stats-overview {
    .header {
      font-size: 1rem;
    }

    div {
      padding: 15px 5px;
    }

    .vertical-header {
      width: calc(1.5em + 10px);
    }
  }
}
</style>
